import React from "react";
import TreeSwing from "../../../images/tree_swing.svg?react";

import "./Collaborations.scss";
import {isEmpty, stopEvent, tagArraySort, unitArraySort} from "../../../utils/Utils";
import I18n from "../../../locale/I18n";
import Entities from "../entities/Entities";
import Button from "../../button/Button";
import {allCollaborationsOptimized, myCollaborationsOptimized} from "../../../api";
import SpinnerField from "../spinner-field/SpinnerField";
import {chipType, isUserAllowed, ROLES} from "../../../utils/UserRole";
import Logo from "../logo/Logo";
import {Chip, ChipType, Tooltip} from "@surfnet/sds";
import ConfirmationDialog from "../../confirmation-dialog/ConfirmationDialog";
import {clearFlash} from "../../../utils/Flash";
import Select, {components as SelectComponents} from "react-select";
import {displayExpiryDate, displayLastActivityDate} from "../../../utils/Date";
import moment from "moment";
import {useQueryParameter, useQueryParameterList} from "../../../hooks/useQueryParameter";

const allValue = "all";

class CollaborationsInner extends React.PureComponent {

    constructor(props, context) {
        super(props, context);
        this.state = {
            standalone: false,
            collaborations: [],
            selectedCollaborations: {},
            filterOptions: [],
            filterValue: [],
            unitFilterOptions: [],
            unitFilterValue: {},
            confirmationDialogOpen: false,
            confirmationTxt: I18n.t("confirmationDialog.confirm"),
            confirmationDialogAction: () => true,
            cancelDialogAction: () => this.setState({confirmationDialogOpen: false}),
            confirmationQuestion: "",
            loading: true
        }
    }

    componentDidMount = () => {
        const {collaborations, user, organisation} = this.props;
        if (collaborations === undefined) {
            (user.admin ? allCollaborationsOptimized() : myCollaborationsOptimized())
                .then(res => {
                    const allFilterOptions = this.allLabelFilterOptions(res);
                    const allUnitFilterOptions = this.allUnitFilterOptions(res);
                    this.addRoleInformation(user, res);
                    this.setState({
                        standalone: true,
                        collaborations: res,
                        filterOptions: allFilterOptions,
                        filterValue: allFilterOptions.filter(o => this.props.queryLabelFilterValue.includes(o.value)),
                        unitFilterOptions: allUnitFilterOptions,
                        unitFilterValue: allUnitFilterOptions.find(o => o.value === this.props.queryUnitFilterValue) || allUnitFilterOptions[0],
                        loading: false
                    });

                })
        } else {
            const allFilterOptions = this.allLabelFilterOptions(collaborations);
            const allUnitFilterOptions = this.allUnitFilterOptions(collaborations, organisation);
            this.addRoleInformation(user, collaborations);
            this.addLabelInformation(collaborations);
            this.setState({
                filterOptions: allFilterOptions,
                filterValue: allFilterOptions.filter(o => this.props.queryLabelFilterValue.includes(o.value)),
                unitFilterOptions: allUnitFilterOptions,
                unitFilterValue: allUnitFilterOptions.find(o => o.value === this.props.queryUnitFilterValue) || allUnitFilterOptions[0],
                loading: false
            })
        }
    }

    addRoleInformation = (user, collaborations) => {
        collaborations.forEach(co => {
            const membership = (user.collaboration_memberships || []).find(m => m.collaboration_id === co.id);
            co.role = membership ? membership.role : null;
        });
    }

    addLabelInformation = collaborations => {
        collaborations.forEach(co => co.tagValues = co.tags
            .sort((t1, t2) => t1.tag_value.localeCompare(t2.tag_value))
            .join(""));
    }

    allLabelFilterOptions = (collaborations) => {
        return collaborations.reduce((acc, coll) => {
            coll.tags.forEach(tag => {
                const option = acc.find(opt => opt.val === tag.tag_value);
                if (option) {
                    ++option.nbr;
                } else {
                    acc.push({val: tag.tag_value, nbr: 1})
                }
            })
            return acc;
        }, []).map(option => ({
            label: `${option.val} (${option.nbr})`,
            value: option.val
        })).sort((o1, o2) => o1.label.localeCompare(o2.label));
    }

    allUnitFilterOptions = (collaborations, organisation) => {
        const unitFilterOptions = [{
            label: I18n.t("units.filter", {nbr: collaborations.length}),
            value: allValue
        }];
        let unitOptions;
        if (organisation) {
            unitOptions = organisation.units
                .map(unit => ({
                    val: unit.name,
                    nbr: collaborations.filter(coll => coll.units.some(u => u.name === unit.name)).length
                }));

        } else {
            unitOptions = collaborations.reduce((acc, coll) => {
                coll.units.forEach(unit => {
                    const option = acc.find(opt => opt.val === unit.name);
                    if (option) {
                        ++option.nbr;
                    } else {
                        acc.push({id: unit.name, val: unit.name, nbr: 1})
                    }
                })
                return acc;
            }, [])
        }
        unitOptions = unitOptions.map(option => ({
            label: `${option.val} (${option.nbr})`,
            value: option.val
        })).sort((o1, o2) => o1.label.localeCompare(o2.label));
        return unitFilterOptions.concat(unitOptions);
    }

    noCollaborations = (organisation, newLabel, newPath, mayCreateCollaborations) => {
        const info = I18n.t(`models.collaborations.noCollaborations${mayCreateCollaborations ? "" : "Request"}`)
        return (
            <div className="no-collaborations">
                <TreeSwing/>
                <p>{info}</p>
                <Button txt={newLabel}
                        onClick={() => {
                            this.props.history.push(newPath)
                        }}/>

            </div>
        )
    }

    openCollaboration = collaboration => e => {
        if (e.metaKey || e.ctrlKey) {
            return;
        }
        stopEvent(e);
        clearFlash();
        this.props.history.push(`/collaborations/${collaboration.id}`);
    };

    LabelFilterValueContainer = ({ children, getValue, selectProps, ...props }) => {
        const count = getValue().length;
        const label = count > 0
            ? I18n.t("models.collaborations.labelsSelected", {nbr: count})
            : selectProps.placeholder;
        return (
            <SelectComponents.ValueContainer {...props} getValue={getValue} selectProps={selectProps}>
                <SelectComponents.SingleValue {...props}>
                    {label}
                </SelectComponents.SingleValue>
                {/* Helps preserve keyboard/focus functionality */}
                {React.Children.map(children, child => child?.type?.name === "DummyInput" ? child : null)}
            </SelectComponents.ValueContainer>
        );
    }

    filters = (organisation, filterOptions, filterValue, unitFilterOptions, unitFilterValue, collaborations) => {
        return (
            <div className={"collaboration-filter-container"}>
                {(!organisation || unitFilterOptions.length > 1) &&
                    <div className="collaboration-filter">
                        <Select
                            className={"collaboration-unit-filter-select"}
                            value={unitFilterValue}
                            classNamePrefix={"filter-select"}
                            onChange={option => {
                                this.props.setQueryUnitFilterValue(option.value);
                                this.setState({unitFilterValue: option});
                            }}
                            options={unitFilterOptions}
                            isSearchable={false}
                            isClearable={false}
                        />
                    </div>}
                {(!organisation || filterOptions.length > 1) && <div className="collaboration-filter">
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        className={"collaboration-label-filter-select"}
                        value={filterValue}
                        placeholder={I18n.t("models.collaborations.allLabels", {nbr: collaborations.length})}
                        classNamePrefix={"filter-select"}
                        onChange={options => {
                            const selected = options || [];
                            this.props.setQueryLabelFilterValue(selected.map(o => o.value));
                            this.setState({filterValue: selected});
                        }}
                        components={{
                            ValueContainer: this.LabelFilterValueContainer,
                            MultiValue: () => null,
                        }}
                        options={filterOptions}
                        isSearchable={false}
                        isClearable={true}
                    />
                </div>}
            </div>
        );
    }

    render() {
        const {
            loading,
            standalone,
            filterOptions,
            filterValue,
            unitFilterOptions,
            unitFilterValue,
            confirmationDialogOpen,
            cancelDialogAction,
            confirmationDialogAction,
            confirmationQuestion,
            confirmationTxt
        } = this.state;
        if (loading) {
            return <SpinnerField/>;
        }
        const {collaborations} = standalone ? this.state : this.props;
        const {organisation} = this.props;

        const {user} = this.props;
        const isOrgManager = isUserAllowed(ROLES.ORG_MANAGER, user);
        const organisationsFromUserSchacHome = user.organisations_from_user_schac_home || [];
        const createFromSchacHome = organisationsFromUserSchacHome
            .some(org => org.collaboration_creation_allowed || org.collaboration_creation_allowed_entitlement)
        const mayCreateCollaborations = isOrgManager || createFromSchacHome;
        const mayRequestCollaboration = !isEmpty(organisationsFromUserSchacHome);
        const organisationQueryParam = organisation ? `?organisationId=${organisation.id}` : "";
        const newLabel = I18n.t(`models.${mayCreateCollaborations ? "collaborations" : "memberCollaborations"}.new`);

        if (isEmpty(collaborations) && !loading) {
            return this.noCollaborations(organisation, newLabel, `/new-collaboration${organisationQueryParam}`, mayCreateCollaborations);
        }

        const columns = [];
        const displayUnitColumn = !organisation || unitFilterOptions.length > 1;
        const displayLabelColumn = !organisation || filterOptions.length > 1;
        columns.push(
            {
                nonSortable: true,
                key: "logo",
                header: "",
                mapper: collaboration => <Logo src={collaboration.logo}/>
            },
            {
                key: "name",
                class: `${displayLabelColumn ? "" : "no-labels"} ${displayUnitColumn ? "" : "no-units"}`,
                header: I18n.t("models.collaborations.name"),
                mapper: collaboration => <a href={`/collaborations/${collaboration.id}`}
                                            className={"neutral-appearance"}
                                            onClick={this.openCollaboration(collaboration)}>{collaboration.name}</a>
            },
            displayUnitColumn ? {
                key: "units",
                class: "units",
                header: I18n.t("units.column"),
                customSort: unitArraySort,
                mapper: collaboration => <div className="unit-container">
                    {collaboration.units
                        .sort((u1, u2) => u1.name.localeCompare(u2.name))
                        .map((unit, index) => <span key={index} className="chip-container">
                        {unit.name}</span>)}
                </div>
            } : null,
            displayLabelColumn ? {
                key: organisation ? "tagValues" : "organisation__name",
                class: "",
                header: organisation ? I18n.t("collaboration.tags") : I18n.t("models.serviceCollaborations.organisationName"),
                customSort: tagArraySort,
                mapper: collaboration => organisation ? collaboration.tags
                    .sort((t1, t2) => t1.tag_value.localeCompare(t2.tag_value))
                    .map((tag, index) => <span key={index}
                                               className={"collaboration_tag"}>{tag.tag_value}</span>) : collaboration.organisation.name
            } : null,
            {
                key: "role",
                class: "",
                header: I18n.t("profile.yourRole"),
                mapper: collaboration => {
                    if (collaboration.role) {
                        return <Chip label={I18n.t(`profile.${collaboration.role}`)}
                                     type={chipType(collaboration)}/>
                    }
                    return null;
                }
            },
            {
                key: "expiry_date",
                class: `expiry_date ${displayLabelColumn ? "" : "no-labels"} ${displayUnitColumn ? "" : "no-units"}`,
                header: I18n.t("collaboration.expiryDate"),
                mapper: collaboration => {
                    if (collaboration.expiry_date) {
                        const today = new Date().getTime();
                        const expiryDate = collaboration.expiry_date * 1000;
                        const days = Math.max(1, Math.round((expiryDate - today) / (1000 * 60 * 60 * 24)));
                        const warning = days < 60;
                        let children;
                        if (collaboration.status === "expired" || warning) {
                            children = <Chip
                                label={displayExpiryDate(collaboration.expiry_date)}
                                type={collaboration.status === "expired" ? ChipType.Main_500 : ChipType.Main_300}/>
                        } else {
                            children = <span className={`expiry-date`}>
                                    {displayExpiryDate(collaboration.expiry_date)}
                                </span>
                        }
                        return <div>
                            <Tooltip standalone={true}
                                     children={children}
                                     tip={moment(expiryDate).format("LL")}>
                            </Tooltip>
                        </div>;
                    }
                    return I18n.t("expirations.never");
                }
            },
            {
                key: "last_activity_date",
                class: `last_activity_date ${displayLabelColumn ? "" : "no-labels"} ${displayUnitColumn ? "" : "no-units"}`,
                header: I18n.t("collaboration.lastActivityDate"),
                mapper: collaboration => {
                    const today = new Date().getTime();
                    const lastActivityDate = collaboration.last_activity_date * 1000;
                    const days = Math.round((today - lastActivityDate) / (1000 * 60 * 60 * 24));
                    const warning = days > 275;
                    return (
                        <Tooltip tip={moment(lastActivityDate).format("LLLL")}
                                 children={
                                     collaboration.status === "suspended" ? <div>
                                         <Chip label={I18n.t("collaboration.lastActivitySuspended")}
                                               type={ChipType.Status_error}/>
                                     </div> : <span className={`${warning ? "warning" : ""}`}>
                                             {displayLastActivityDate(collaboration.last_activity_date)}
                                         </span>
                                 }
                                 standalone={true}/>

                    );
                }
            }, {
                key: "collaboration_memberships_count",
                header: I18n.t("models.collaborations.memberCount")
            }
        );
        const selectedLabels = filterValue.map(o => o.value);
        let filteredCollaborations = selectedLabels.length === 0
            ? collaborations
            : collaborations.filter(coll => coll.tags.some(tag => selectedLabels.includes(tag.tag_value)));
        filteredCollaborations = unitFilterValue.value === allValue ? filteredCollaborations :
            filteredCollaborations.filter(coll => coll.units.some(unit => unit.name === unitFilterValue.value));
        return (
            <>
                <ConfirmationDialog isOpen={confirmationDialogOpen}
                                    cancel={cancelDialogAction}
                                    confirm={confirmationDialogAction}
                                    isWarning={true}
                                    confirmationTxt={confirmationTxt}
                                    question={confirmationQuestion}/>
                <Entities entities={filteredCollaborations}
                          modelName={mayCreateCollaborations ? "collaborations" : mayRequestCollaboration ? "memberCollaborations" : "collaborations"}
                          searchAttributes={["name", "identifier", "short_name"]}
                          defaultSort="name"
                          inputFocus={true}
                          title={`${I18n.t("home.tabs.collaborations")} (${collaborations.length})`}
                          rowLinkMapper={() => this.openCollaboration}
                          columns={columns.filter(col => !isEmpty(col))}
                          onHover={true}
                          newLabel={newLabel}
                          filters={this.filters(organisation, filterOptions, filterValue, unitFilterOptions, unitFilterValue, collaborations)}
                          actionHeader={"collaboration-services"}
                          showNew={mayCreateCollaborations || mayRequestCollaboration}
                          newEntityPath={`/new-collaboration${organisationQueryParam}`}
                          loading={loading}
                          {...this.props}/>
            </>)
    }

}

const Collaborations = (props) => {
    const [queryLabelFilterValue, setQueryLabelFilterValue] = useQueryParameterList('label');
    const [queryUnitFilterValue, setQueryUnitFilterValue] = useQueryParameter('unit');
    return <CollaborationsInner {...props}
                                queryLabelFilterValue={queryLabelFilterValue}
                                setQueryLabelFilterValue={setQueryLabelFilterValue}
                                queryUnitFilterValue={queryUnitFilterValue}
                                setQueryUnitFilterValue={setQueryUnitFilterValue}/>;
};

export default Collaborations;
